import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CarCard from "../components/CarCard";
import { carsApi, inquiriesApi } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { Loader2, Sparkles, Bell, Send } from "lucide-react";

export default function Upcoming() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", car_id: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await carsApi.list({ upcoming: "only", light: 1 });
        setCars(list);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      await inquiriesApi.create({
        type: "upcoming",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        car_id: form.car_id || null,
        subject: t("upcoming.notifyTitle"),
        message: form.message.trim() || null,
      });
      toast({ title: t("upcoming.notifySent"), description: t("upcoming.notifySentDesc") });
      setForm({ name: "", email: "", phone: "", car_id: "", message: "" });
    } catch (err) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />

      <section className="pt-[140px] pb-14 px-5 lg:px-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-[#c5a76f] text-xs tracking-[0.3em] uppercase mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {t("upcoming.overline")}
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            style={{ fontFamily: "'Familjen Grotesk', serif" }}
          >
            {t("upcoming.title1")} <span className="text-[#c5a76f]">{t("upcoming.titleAccent")}</span>{" "}
            {t("upcoming.title2")}
          </h1>
          <p className="mt-5 text-white/65 max-w-2xl text-base leading-relaxed">
            {t("upcoming.lead")}
          </p>
        </div>
      </section>

      <section className="py-16 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="py-20 text-center text-white/40">
              <Loader2 className="inline w-6 h-6 animate-spin" />
            </div>
          ) : cars.length === 0 ? (
            <div className="py-20 text-center text-white/55" data-testid="upcoming-empty">
              {t("upcoming.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="upcoming-grid">
              {cars.map((c) => (
                <CarCard key={c.id} car={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Notify form */}
      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[900px] mx-auto bg-[#242424] border border-[#c5a76f]/30 rounded-lg p-8 md:p-12">
          <div className="flex items-start gap-4 mb-8">
            <div className="shrink-0 w-12 h-12 rounded-full bg-[#c5a76f]/10 border border-[#c5a76f]/30 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#c5a76f]" />
            </div>
            <div>
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "'Familjen Grotesk', serif" }}
              >
                {t("upcoming.notifyTitle")}
              </h2>
              <p className="text-white/65 text-sm leading-relaxed">{t("upcoming.notifyLead")}</p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="upcoming-notify-form">
            <Field label={t("common.name")} required>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="upcoming-input-name"
                className="inp"
              />
            </Field>
            <Field label={t("common.email")} required>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="upcoming-input-email"
                className="inp"
              />
            </Field>
            <Field label={t("common.phone")}>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="upcoming-input-phone"
                className="inp"
              />
            </Field>
            <Field label={t("upcoming.carSelect")}>
              <select
                value={form.car_id}
                onChange={(e) => setForm({ ...form, car_id: e.target.value })}
                data-testid="upcoming-input-car"
                className="inp"
              >
                <option value="">{t("upcoming.noSpecificCar")}</option>
                {cars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label={t("common.message")}>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="upcoming-input-message"
                  className="inp"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="upcoming-submit-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] disabled:opacity-50 transition-colors text-sm font-semibold tracking-widest uppercase rounded"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t("upcoming.notifyCta")}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />

      <style>{`
        .inp {
          background: #1a1a1a;
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 0.375rem;
          width: 100%;
          font-size: 0.875rem;
          transition: border-color .15s;
        }
        .inp:focus { outline: none; border-color: #c5a76f; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[#c5a76f] text-[10px] uppercase tracking-[0.2em] mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
    </label>
  );
}
